/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {MaterialFilledBackIcon} from '@ion/dls/src/icons/MaterialFilledBackIcon';
import {MaterialFilledForwardIcon} from '@ion/dls/src/icons/MaterialFilledForwardIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {Color, IconSize, NavigationIconDirectionEnum} from '../types/types';

type Props = {
  direction?: keyof typeof NavigationIconDirectionEnum;
  isActive?: boolean;
  color?: Color;
  size?: IconSize;
};

/**
 * @NavigationIcon
 *
 * Grey Icon for Back Navigation
 *
 * ```typescript
 * import React from 'react';
 * import { NavigationIcon } from '@ion/dls/src/icons/';
 *
 * function MyComponent {
 *   return (
 *     <>
 *       <NavigationIcon />
 *       <NavigationIcon direction="forward" />
 *     </>
 *   );
 * }
 *
 * ```
 *
 */
export const NavigationIcon = ({
  isActive,
  color,
  direction = 'back',
  size = IconSize.large
}: Props) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const {isCurrent: isCurrentTheme}   = useThemeContext();

  function getColor(isMouseDown, isMouseOver) {
    return isMouseDown ? dlsColor('grey1') : isMouseOver ? dlsColor('grey1')
      : isActive ? dlsColor('grey1') : dlsColor('grey3');
  }

  const iconProps = {
    size,
    color : color || getColor(isMouseDown, isMouseOver)
  };

  // a plain <span> is display:inline by default, so an explicit
  // height/width never actually applies to it regardless of the icon's own
  // size - inline-flex makes this span a sized box that actually matches
  // the icon it wraps. Current theme only - this changes baseline/box
  // behavior, so every other (legacy-reachable) consumer of this icon
  // keeps its original plain inline span untouched ...
  const wrapperStyle = isCurrentTheme ? {display: 'inline-flex' as const, height: size, width: size} : undefined;

  return (
    <span style={wrapperStyle}
      onMouseLeave={function() {
        setIsMouseOver(false);
      }} onMouseDown={function() {
        setIsMouseDown(true);
      }} onMouseUp={function() {
        setIsMouseDown(false);
      }} onMouseEnter={function() {
        setIsMouseOver(true);
      }}>
      {direction === 'back' && <MaterialFilledBackIcon {...iconProps} />}
      {direction === 'forward' && <MaterialFilledForwardIcon {...iconProps} />}
    </span>
  );
};
